import React from 'react';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';
import { Divider } from '@usepepper/ui/atoms/divider';
import { PRICING_TABLE_LAYOUT } from '@sales-portal/domain/pricing/constants';
import { View } from 'react-native';

const ROW_COUNT = 5;
const ROW_HEIGHT = 32;
const BASE_COLUMN_WIDTH = 60;

const getFirstColumnMinWidth = (columnIndex: number) => ({
  minWidth: columnIndex === 0 ? 220 : BASE_COLUMN_WIDTH,
});

export const PricingTableSkeletonRows: React.FC<{ rowCount?: number }> = ({
  rowCount = ROW_COUNT,
}) => {
  const [styles] = useStyles();

  return (
    <View accessibilityState={{ busy: true }} style={styles.body}>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <View key={rowIndex} style={{ opacity: 1 - rowIndex * 0.2 }}>
          <View style={styles.row}>
            {Array.from({
              length: PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT + 1,
            }).map((_, colIndex) => (
              <View
                key={colIndex}
                style={[styles.baseColumn, getFirstColumnMinWidth(colIndex)]}
              >
                <AnimatedSkeletonLoader height={14} width={62} />
              </View>
            ))}
          </View>
          <Divider key={rowIndex} />
        </View>
      ))}
    </View>
  );
};

export const PricingTableSkeleton = () => {
  const [styles] = useStyles();

  return (
    <View
      accessibilityState={{ busy: true }}
      style={styles.container}
      testID="pricing_table_skeleton"
    >
      <View style={styles.header}>
        {Array.from({
          length: PRICING_TABLE_LAYOUT.REGULAR_COLUMN_COUNT + 1,
        }).map((_, headerIndex) => (
          <View
            key={headerIndex}
            style={[
              styles.baseHeaderColumn,
              getFirstColumnMinWidth(headerIndex),
            ]}
          >
            <AnimatedSkeletonLoader height={12} width={24} />
          </View>
        ))}
      </View>
      <Divider />
      <PricingTableSkeletonRows rowCount={ROW_COUNT} />
    </View>
  );
};

const useStyles = createStyles(() => ({
  baseColumn: {
    alignItems: 'flex-start',
    justifyContent: 'center',
    minWidth: BASE_COLUMN_WIDTH,
  },
  baseHeaderColumn: {
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    minWidth: BASE_COLUMN_WIDTH,
  },
  body: {
    flexDirection: 'column',
  },
  container: {
    paddingHorizontal: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: ROW_HEIGHT,
    paddingBottom: 12,
  },

  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: ROW_HEIGHT,
    paddingVertical: 8,
  },
}));
